import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    LinearProgress,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Sync as SyncIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Schedule as ScheduleIcon,
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    Refresh as RefreshIcon,
    AccountTree as AccountTreeIcon,
    MedicalServices as MedicalServicesIcon,
    CalendarToday as CalendarTodayIcon,
    EventNote as EventNoteIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import SyncConfigDialog from '../components/SyncConfigDialog';
import { syncBookingsFromRecup } from '../services/bookingService';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const SyncPage: React.FC = () => {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [syncStatus, setSyncStatus] = useState<any>(null);
    const [syncLogs, setSyncLogs] = useState<any[]>([]);
    const [autoSyncRunning, setAutoSyncRunning] = useState(false);
    const [configDialogOpen, setConfigDialogOpen] = useState(false);

    useEffect(() => {
        loadSyncStatus();
        loadSyncLogs();
    }, []);

    const loadSyncStatus = async () => {
        try {
            const response = await api.get('/recup-sync/status');
            setSyncStatus(response.data);
            setAutoSyncRunning(response.data.autoSyncRunning || false);
        } catch (err: any) {
            console.error('Error loading sync status:', err);
        }
    };

    const loadSyncLogs = async () => {
        try {
            const response = await api.get('/recup-sync/logs', {
                params: { limit: 10 }
            });
            setSyncLogs(response.data.data || []);
        } catch (err: any) {
            console.error('Error loading sync logs:', err);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const testConnection = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await api.get('/recup-sync/test-connection');
            if (response.data.success) {
                setSuccess('Connessione a ReCup riuscita!');
            } else {
                setError(response.data.message || 'Connessione fallita');
            }
            loadSyncStatus();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore nella connessione');
        } finally {
            setLoading(false);
        }
    };

    const syncWithHierarchy = () => {
        navigate('/hierarchical-structure');
    };

    const performFullSync = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await api.post('/recup-sync/sync/full');
            if (response.data.success) {
                setSuccess('Sincronizzazione completa eseguita con successo');
                loadSyncLogs();
                loadSyncStatus();
            } else {
                setError('Sincronizzazione fallita');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore durante la sincronizzazione');
        } finally {
            setLoading(false);
        }
    };

    const toggleAutoSync = async () => {
        setLoading(true);
        setError(null);

        try {
            if (autoSyncRunning) {
                await api.post('/recup-sync/sync/auto/stop');
                setSuccess('Sincronizzazione automatica fermata');
                setAutoSyncRunning(false);
            } else {
                await api.post('/recup-sync/sync/auto/start', {
                    intervalMinutes: 5
                });
                setSuccess('Sincronizzazione automatica avviata (ogni 5 minuti)');
                setAutoSyncRunning(true);
            }
            loadSyncStatus();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Errore nel controllo sincronizzazione automatica');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomSync = async (options: any) => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setConfigDialogOpen(false);

        try {
            const result = await syncBookingsFromRecup(options);
            
            if (result.success) {
                setSuccess(
                    `Sincronizzazione completata con successo! 
                     Processati: ${result.processed || 0}, 
                     Creati: ${result.created || 0}, 
                     Aggiornati: ${result.updated || 0}, 
                     Errori: ${result.errors || 0}`
                );
            } else {
                setError(result.error || 'Sincronizzazione fallita');
            }
            
            loadSyncLogs();
            loadSyncStatus();
        } catch (err: any) {
            if (err.confirmationRequired) {
                setError(`Trovati ${err.existingCount} record esistenti. Confermare per procedere.`);
            } else {
                setError(err.response?.data?.message || err.message || 'Errore durante la sincronizzazione personalizzata');
            }
        } finally {
            setLoading(false);
        }
    };

    const getLogStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircleIcon color="success" />;
            case 'failed':
                return <ErrorIcon color="error" />;
            case 'in_progress':
                return <CircularProgress size={20} />;
            default:
                return <InfoIcon color="info" />;
        }
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleString('it-IT');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h5" component="h2">
                                    <SyncIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Sincronizzazione ReCup
                                </Typography>
                                <Box>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={loadSyncStatus}
                                        disabled={loading}
                                        sx={{ mr: 1 }}
                                    >
                                        Aggiorna Stato
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AccountTreeIcon />}
                                        onClick={syncWithHierarchy}
                                    >
                                        Sincronizza da Struttura
                                    </Button>
                                </Box>
                            </Box>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                                    {success}
                                </Alert>
                            )}

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Stato Servizio
                                        </Typography>
                                        <Chip
                                            label={syncStatus?.initialized ? 'Inizializzato' : 'Non Inizializzato'}
                                            color={syncStatus?.initialized ? 'success' : 'default'}
                                            sx={{ mt: 1 }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Token OAuth
                                        </Typography>
                                        <Chip
                                            label={syncStatus?.hasValidToken ? 'Valido' : 'Scaduto/Assente'}
                                            color={syncStatus?.hasValidToken ? 'success' : 'error'}
                                            sx={{ mt: 1 }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Sincronizzazione Auto
                                        </Typography>
                                        <Chip
                                            label={autoSyncRunning ? 'Attiva' : 'Disattiva'}
                                            color={autoSyncRunning ? 'success' : 'default'}
                                            sx={{ mt: 1 }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Ultima Sincronizzazione
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            {syncStatus?.lastSyncTime 
                                                ? formatDate(syncStatus.lastSyncTime)
                                                : 'Mai eseguita'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Tabs value={tabValue} onChange={handleTabChange}>
                                <Tab label="Azioni Manuali" />
                                <Tab label="Log Sincronizzazioni" />
                                <Tab label="Configurazione" />
                            </Tabs>

                            <TabPanel value={tabValue} index={0}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Test Connessione
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    Verifica la connessione e l'autenticazione con le API ReCup
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    onClick={testConnection}
                                                    disabled={loading}
                                                    startIcon={loading ? <CircularProgress size={20} /> : <SyncIcon />}
                                                >
                                                    Test Connessione
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Sincronizzazione Completa
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    Sincronizza tutti i dati dalla struttura gerarchica
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    onClick={performFullSync}
                                                    disabled={loading}
                                                    startIcon={loading ? <CircularProgress size={20} /> : <SyncIcon />}
                                                >
                                                    Sincronizzazione Completa
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Sincronizzazione Personalizzata
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    Configura parametri personalizzati per la sincronizzazione
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={() => setConfigDialogOpen(true)}
                                                    disabled={loading}
                                                    startIcon={<SettingsIcon />}
                                                >
                                                    Configura Sync
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Controllo Auto-Sync
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    Avvia o ferma la sincronizzazione automatica
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color={autoSyncRunning ? "error" : "success"}
                                                    onClick={toggleAutoSync}
                                                    disabled={loading}
                                                    startIcon={loading ? <CircularProgress size={20} /> : 
                                                              (autoSyncRunning ? <StopIcon /> : <PlayArrowIcon />)}
                                                >
                                                    {autoSyncRunning ? 'Ferma Auto-Sync' : 'Avvia Auto-Sync'}
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Alert severity="info">
                                            <Typography variant="subtitle2" gutterBottom>
                                                Workflow di Sincronizzazione Corretto:
                                            </Typography>
                                            <ol style={{ marginTop: 8, paddingLeft: 20 }}>
                                                <li>Vai alla pagina "Struttura Gerarchica" per recuperare la struttura dal codice NSIS</li>
                                                <li>Seleziona le unità eroganti da sincronizzare</li>
                                                <li>Avvia la sincronizzazione delle unità selezionate</li>
                                                <li>Le agende associate verranno sincronizzate automaticamente</li>
                                            </ol>
                                        </Alert>
                                    </Grid>
                                </Grid>
                            </TabPanel>

                            <TabPanel value={tabValue} index={1}>
                                <List>
                                    {syncLogs.map((log, index) => (
                                        <React.Fragment key={log.id}>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {getLogStatusIcon(log.status)}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="subtitle1">
                                                                {log.sync_type}
                                                            </Typography>
                                                            <Chip 
                                                                label={log.status} 
                                                                size="small"
                                                                color={log.status === 'completed' ? 'success' : 'error'}
                                                            />
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="body2">
                                                                Iniziato: {formatDate(log.started_at)}
                                                                {log.completed_at && ` - Completato: ${formatDate(log.completed_at)}`}
                                                            </Typography>
                                                            {log.records_processed && (
                                                                <Typography variant="body2">
                                                                    Processati: {log.records_processed} | 
                                                                    Creati: {log.records_created} | 
                                                                    Aggiornati: {log.records_updated} | 
                                                                    Falliti: {log.records_failed}
                                                                </Typography>
                                                            )}
                                                            {log.error_message && (
                                                                <Typography variant="body2" color="error">
                                                                    Errore: {log.error_message}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                            {index < syncLogs.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </TabPanel>

                            <TabPanel value={tabValue} index={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Sincronizzazione Automatica
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" paragraph>
                                                    Abilita la sincronizzazione automatica ogni 5 minuti
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color={autoSyncRunning ? 'error' : 'primary'}
                                                    onClick={toggleAutoSync}
                                                    disabled={loading}
                                                    startIcon={autoSyncRunning ? <StopIcon /> : <PlayArrowIcon />}
                                                >
                                                    {autoSyncRunning ? 'Ferma Auto-Sync' : 'Avvia Auto-Sync'}
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </TabPanel>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            
            <SyncConfigDialog
                open={configDialogOpen}
                onClose={() => setConfigDialogOpen(false)}
                onSync={handleCustomSync}
                loading={loading}
            />
        </Box>
    );
};

export default SyncPage;